import React, { useRef, useEffect, useState, useCallback, memo } from "react";
import {
  Object3D,
  BufferGeometry,
  AudioListener,
  AudioLoader,
  AudioAnalyser,
  Audio,
  FogExp2,
  BufferAttribute
} from "three";
import {
  GodRaysEffect,
  RenderPass,
  EffectPass,
  EffectComposer,
  SMAAEffect,
  SMAAPreset
} from "postprocessing";
import { isMobile } from "react-device-detect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVolumeUp, faVolumeMute } from "@fortawesome/free-solid-svg-icons";
import debounce from "lodash.debounce";

import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { DeviceOrientationControls } from "three-stdlib";

import useWindowSize from "../hooks/useWindowSize";

import "../style/three.css";

import {
  setupRenderer,
  setupCamera,
  setupScene,
  setupLights,
  setupCircle,
  loadText,
  createFontMesh,
  createFontLine,
  setupStars
} from "../threejs";

import { colorPalette, fftSize } from "../threejs/config";

const { NIGHT_OWL_BLUE } = colorPalette;

let frameId: number | null;

// Three Scene init setup start
const scene = setupScene(NIGHT_OWL_BLUE);

const lights = setupLights();
scene.add(...lights);

const circle = setupCircle();
scene.add(circle);

const smaaEffect = new SMAAEffect({ preset: SMAAPreset.MEDIUM });

const stars = setupStars();
scene.add(stars);

const textGeometry = loadText();
const textMesh = createFontMesh(textGeometry);

const textLine = createFontLine(textGeometry);

const textObj = new Object3D().add(textMesh, textLine);
textObj.position.set(-150, -90, 0);
scene.add(textObj);

let audioLoader: AudioLoader;
let listener: AudioListener;
let sound: Audio;
let analyser: AudioAnalyser;

const camera = setupCamera(window.innerWidth, window.innerHeight);

const init = async () => {
  audioLoader = new AudioLoader();
  listener = new AudioListener();
  await listener.context.resume();
  sound = new Audio(listener);
  analyser = new AudioAnalyser(sound, fftSize);
  camera.add(listener);
};

const godRaysEffect = new GodRaysEffect(camera, circle, {
  resolutionScale: 1,
  density: 0.9,
  decay: 0.95,
  weight: 0.4,
  samples: 100
});

const renderPass = new RenderPass(scene, camera);
const effectPass = new EffectPass(camera, smaaEffect, godRaysEffect);
effectPass.renderToScreen = true;
const renderer = setupRenderer(window.innerWidth, window.innerHeight);
const composer = new EffectComposer(renderer);
composer.addPass(renderPass);
composer.addPass(effectPass);

const orbitControls = new OrbitControls(camera, renderer.domElement);
orbitControls.enablePan = false;
orbitControls.maxDistance = 1500;
orbitControls.minDistance = 200;
orbitControls.rotateSpeed = 0.4;
orbitControls.zoomSpeed = 0.6;

const renderScene = () => {
  composer.render();
};

const starsMove = (val = 0.05) => {
  const starGeo = stars.geometry as BufferGeometry & {
    userData: { velocities: number[] };
  };
  const positions = starGeo.getAttribute("position") as BufferAttribute;
  const velocities = starGeo.userData.velocities as number[];
  for (let i = 0; i < positions.count; i++) {
    velocities[i] += val + Math.random() * 0.01;
    let z = positions.getZ(i) + velocities[i];
    if (z > 1500) {
      z = -1500;
      velocities[i] = 0;
    }
    positions.setZ(i, z);
  }
  positions.needsUpdate = true;
  stars.rotation.z -= 0.0005;
};
// Three Scene init setup end

const ThreeScene = () => {
  const [isClicked, setIsClicked] = useState(false);
  const [isMesh, setFontForm] = useState(true);
  const [isAudioLoaded, setAudioLoadingStatus] = useState(false);
  const [isMuted, setMuteStatus] = useState(false);
  const [isOrientControl, setChecked] = useState(false);

  const debounceSetFontForm = useCallback(debounce(setFontForm, 10), []);

  textMesh.visible = isMesh;
  textLine.visible = !isMesh;

  const divRef = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowSize();

  let controls: null | DeviceOrientationControls;

  if (isMobile) {
    controls = new DeviceOrientationControls(camera);
    controls.deviceOrientation = {} as Partial<DeviceOrientationEvent>;
    scene.fog = null;
  } else {
    if (scene.fog === null) {
      scene.fog = new FogExp2(NIGHT_OWL_BLUE, 0.0005);
    }
  }

  const onWindowResize = (width: number, height: number) => {
    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    composer.setSize(width, height);
  };

  const animate = () => {
    isMobile && controls && controls.update();
    frameId = window.requestAnimationFrame(animate);

    const audioData = analyser && analyser.getFrequencyData();

    if (audioData) {
      const bassDrum = audioData[0];
      const snareDrum = audioData[41];

      if (bassDrum >= 235) {
        // do something when bass drum hit
      } else if (bassDrum > 0 && bassDrum <= 195) {
        // go back to previous status after bass drum hit
      }

      if (snareDrum >= 155) {
        debounceSetFontForm(false);
      } else if (snareDrum > 0 && snareDrum <= 100) {
        debounceSetFontForm(true);
      }
    }

    starsMove();
    renderScene();
  };

  const start = () => {
    if (!frameId) {
      frameId = requestAnimationFrame(animate);
    }
  };

  const stop = () => {
    frameId && cancelAnimationFrame(frameId);
    frameId = null;
  };

  useEffect(() => {
    isClicked &&
      !isAudioLoaded &&
      audioLoader &&
      audioLoader.load(
        `${process.env.PUBLIC_URL}/BlueBoi.mp3`,
        (buffer: AudioBuffer) => {
          if (sound) {
            sound.setBuffer(buffer);
            sound.setLoop(true);
            setAudioLoadingStatus(true);
            sound.setVolume(isMuted ? 0 : 1);
            sound.play();
          }
        },
        (xhr: ProgressEvent<EventTarget>) => {
          const loadingStatus = (xhr.loaded / xhr.total) * 100;
          console.info(`Audio Loading: ${Math.floor(loadingStatus)}%`);
          if (loadingStatus === 100) {
            console.log("Loading Complete");
          }
        }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClicked]);

  useEffect(() => {
    onWindowResize(width, height);

    if (!isMobile) {
      start();
    }

    const divEl = divRef.current;
    divEl?.appendChild(renderer.domElement);

    return () => {
      stop();
      divEl?.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  useEffect(
    () => {
      if (isMobile) {
        controls?.connect();
        if (controls) controls.enabled = isOrientControl;
        start();
      }
      return () => {
        controls?.disconnect();
        controls?.dispose();
        stop();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMobile, isOrientControl]
  );

  return (
    <>
      {!isAudioLoaded ? (
        <div className="overlay">
          <div className="overlay--container">
            <q className="quote">
              If you gaze long enough into an abyss, the abyss will gaze back into you.
            </q>
            <small className="author"> -- Friedrich Nietzsche</small>
            <FontAwesomeIcon
              icon={isMuted ? faVolumeMute : faVolumeUp}
              className="volume-control"
              size="2x"
              onClick={() => setMuteStatus(!isMuted)}
            />
            {!isClicked && (
              <button
                className="start-button"
                onClick={() => {
                  init();
                  setIsClicked(true);
                }}
              >
                Start
              </button>
            )}
            {isClicked && (
              <div className="lds-facebook">
                <div></div>
                <div></div>
                <div></div>
              </div>
            )}
            {isMobile && (
              <div className="orientation-control">
                <small className="label">Device Orientation</small>
                <input
                  className="checkbox"
                  type="checkbox"
                  checked={isOrientControl}
                  onChange={() => setChecked(!isOrientControl)}
                />
              </div>
            )}
            <small className="hint">
              {isMobile
                ? isOrientControl
                  ? "Move your device around (or Touch/Pinch) "
                  : "Touch/Pinch your screen "
                : "Darg/Zoom your mouse "}
              to explore
            </small>
          </div>
        </div>
      ) : null}
      <div ref={divRef} className="three-container"></div>

      <footer>
        <small className="music-credit">
          Music by LAKEY INSPIRED -{" "}
          <a href="https://soundcloud.com/lakeyinspired/blue-boi">Blue Boi</a>
        </small>
        <small className="me">
          &copy; {new Date().getFullYear()}{" "}
          <a href="https://haoranzhang.me/" rel="noopener noreferrer" target="_blank">
            Haoran Zhang
          </a>
        </small>
      </footer>
    </>
  );
};

export default memo(ThreeScene);
